import { ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@carbon/react";

export interface ConfirmDialogProps {
  open: boolean;
  /** Renders the confirm action as Carbon's destructive/`danger` button
   * and — see the focus note below — changes where initial focus lands. */
  danger?: boolean;
  /** Already translated by the caller, same convention as `PageHeader`'s
   * `title`. Doubles as the dialog's accessible name (see below). */
  title: string;
  body: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Whether the action `onConfirm` triggers is currently in flight (e.g. a mutation's own
   * `isPending`). While `true`, the confirm button disables itself and swaps its label for
   * Carbon's inline spinner — see the note below on why this has to live here rather than at
   * each call site.
   */
  isPending?: boolean;
  /**
   * Opt-in guard that disables the confirm button while the dialog's own inputs are
   * incomplete — distinct from `isPending` (which is about an in-flight action). Used by
   * dialogs whose body carries a required field: e.g. a whole-request reject must not be
   * confirmable with a blank reason. Defaults to `false` (no extra gating), so every
   * existing caller is unaffected.
   */
  confirmDisabled?: boolean;
}

/**
 * A confirmation dialog on Carbon's `ComposedModal`, for anything that
 * needs an "are you sure?" step before it runs — a destructive one
 * (deactivating a colleague) passes `danger`, everything else doesn't.
 *
 * Two accessibility guarantees this primitive exists to make correct by
 * construction, not by convention at each call site:
 *
 * 1. **The dialog has a real accessible name.** Carbon's `ModalHeader`
 *    renders `title` as a heading, but `ComposedModal` does NOT wire that
 *    heading's id to the dialog's `aria-labelledby` on its own — the
 *    dialog would announce as an unnamed "dialog" unless something sets
 *    it explicitly. This passes `aria-label={title}` straight through so
 *    `getByRole("dialog", { name: title })` (and a screen reader) actually
 *    resolves.
 *
 * 2. **Focus behaves the way a destructive confirmation must.**
 *    `ComposedModal` already moves focus into the dialog on open and
 *    traps it while open (see its `initialFocus`/`wrapFocus` handling) —
 *    nothing to add there. It ALSO already refuses to default-focus the
 *    primary button when `danger` is set, focusing the secondary
 *    (Cancel) button instead, specifically so a stray Return keypress
 *    can't fire the destructive action — this component only has to ask
 *    for `danger` styling and that behaviour follows for free. What
 *    `ComposedModal` does NOT do on its own is restore focus to whatever
 *    triggered the dialog when it closes — that only happens if a
 *    `launcherButtonRef` is threaded through from the trigger, which
 *    isn't part of this (deliberately trigger-agnostic) prop contract.
 *    This component restores it itself, by remembering
 *    `document.activeElement` at the moment `open` turns true and
 *    refocusing it the moment `open` turns false.
 *
 * 3. **A second click while the action is in flight can't fire a second
 *    call.** Nothing about `open`/`onConfirm` alone stops that — a fast
 *    double-click (or a slow network) would submit twice. This wasn't
 *    dangerous for the first caller (`PUT /user/{id}/active` is
 *    idempotent), but that's a property of one endpoint, not something a
 *    shared component gets to assume about the next one. Passing
 *    `isPending` disables the confirm button and — via `ModalFooter`'s own
 *    `loadingStatus`, not a bespoke spinner — swaps its label for Carbon's
 *    `InlineLoading`, so the caller doesn't have to separately disable its
 *    trigger button against re-opening mid-mutation either.
 *
 * Owned by the primitives layer — the "deactivate this user" flow and any
 * later destructive confirmation (site/tag/material removal) consume this
 * verbatim.
 */
const ConfirmDialog = ({
  open,
  danger,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  isPending = false,
  confirmDisabled = false,
}: ConfirmDialogProps) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      triggerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      return;
    }
    triggerRef.current?.focus();
    triggerRef.current = null;
  }, [open]);

  return (
    <ComposedModal
      open={open}
      danger={danger}
      aria-label={title}
      onClose={() => {
        onCancel();
      }}
    >
      <ModalHeader title={title} />
      <ModalBody>{body}</ModalBody>
      <ModalFooter
        danger={danger}
        secondaryButtonText={t("cta.cancel")}
        primaryButtonText={confirmLabel}
        // `loadingStatus="active"` is what actually disables the primary button while
        // `isPending` — see ModalFooter's own implementation, which ORs `loadingActive` into
        // the button's `disabled` regardless of `primaryButtonDisabled`. Passing both is
        // deliberately belt-and-suspenders: `primaryButtonDisabled` keeps the button disabled
        // even in the (currently unused) "finished"/"error" loading states this component
        // doesn't drive.
        loadingStatus={isPending ? "active" : "inactive"}
        loadingDescription={t("cta.loading")}
        // `confirmDisabled` layers an additional, caller-driven gate (e.g. a required-field
        // guard in `body`, such as a reject reason) on top of the in-flight one.
        primaryButtonDisabled={isPending || confirmDisabled}
        // Deliberately NOT wired to `closeModal`/`onRequestClose` as well —
        // Carbon's secondary (Cancel) button already routes through
        // `ComposedModal`'s own `closeModal`, which calls `onClose` above.
        // Wiring `onCancel` a second time here would fire it twice for one
        // Cancel click. The primary button's `onRequestSubmit`, by
        // contrast, is the ONLY thing that calls `onConfirm` — Carbon
        // never routes it through `closeModal`, which is what guarantees
        // `onConfirm` fires from the confirm button and nothing else.
        onRequestSubmit={onConfirm}
      >
        {/* Carbon's `ModalFooterProps.children` is typed as required
            even though nothing renders it when both button-text props
            are supplied and it's runtime-optional (see ModalFooter.js) —
            there's no button content this component needs to add. */}
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default ConfirmDialog;
